import {forwardRef, useMemo} from 'react';
import {MenuItem, Typography} from '@mui/material';

import {ObjectSelect, ObjectSelectProps} from '@shared/components';
import {useGetSubCategories} from '../../../../entities/category/apiHooks';
import {SubCategory} from '../../../../entities/category/types';

type Props = Omit<
  ObjectSelectProps<SubCategory>,
  'options' | 'labelProp' | 'valueProp' | 'value' | 'onChange'
> & {
  categoryId: string | null;
  value: string | null;
  onChange: (subCategoryId: string | null) => void;
  placeholder?: string;
};

const ALL_SUBCATEGORY: SubCategory = {
  id: '',
  name: 'Subcategory',
};

export const SubCategoryFilter = forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const {
      categoryId,
      value: subCategoryId,
      onChange,
      placeholder = 'Subcategory',
      disabled,
      ...restProps
    } = props;

    const {data, isPending} = useGetSubCategories(categoryId!, {
      enabled: Boolean(categoryId),
    });

    const subCategories = data ?? [];

    const options = useMemo(
      () => [ALL_SUBCATEGORY, ...subCategories],
      [subCategories],
    );

    const selectedSubCategory = useMemo<SubCategory>(() => {
      return (
        options.find(sc => sc.id === (subCategoryId ?? '')) ?? ALL_SUBCATEGORY
      );
    }, [options, subCategoryId]);

    const handleChange: ObjectSelectProps<SubCategory>['onChange'] = next => {
      if (!next) {
        onChange(null);
        return;
      }

      if (Array.isArray(next)) {
        const first = next[0];
        onChange(first && first.id !== '' ? first.id : null);
        return;
      }

      onChange(next.id === '' ? null : next.id);
    };

    return (
      <ObjectSelect<SubCategory>
        {...restProps}
        ref={ref}
        disabled={isPending || !categoryId || Boolean(disabled)}
        options={options}
        labelProp="name"
        valueProp="id"
        value={selectedSubCategory}
        onChange={handleChange}
        displayEmpty
        renderValue={value => {
          const id = Array.isArray(value) ? value[0] : value;

          if (!id) {
            return (
              <Typography sx={{color: '#9e9e9e'}}>{placeholder}</Typography>
            );
          }

          const subCategory = options.find(sc => sc.id === id);

          if (!subCategory || subCategory.id === '') {
            return (
              <Typography sx={{color: '#9e9e9e'}}>{placeholder}</Typography>
            );
          }

          return <Typography>{subCategory.name}</Typography>;
        }}
        renderOption={subCategory => {
          if (subCategory.id === '') {
            return (
              <MenuItem key="__all" value="">
                <Typography sx={{color: '#9e9e9e'}}>{placeholder}</Typography>
              </MenuItem>
            );
          }

          return (
            <MenuItem key={subCategory.id} value={subCategory.id}>
              <Typography>{subCategory.name}</Typography>
            </MenuItem>
          );
        }}
      />
    );
  },
);
